import React from "react"
import { Link } from "gatsby"

import {
  communityPromotionLink,
  communityGoldSubscriptionLink,
  communityPromotionSubscriptionLink,
  sponsorPromotionLink,
} from "../components/promotions/constants"

const linkClass = "font-semibold underline text-rose-600"

function submitPageFaq() {
  return [
    {
      question: "How do I list my community on Hive Index?",
      answerPlain:
        "Submit your community through the free listing form on Hive Index. You provide your community name, platform, topic categories, member count, and a short description. Listings are reviewed for relevance and typically appear on up to two matching topic pages within a few days.",
      answer: (
        <>
          Submit your community through the{" "}
          <Link to="/submit/add/" className={linkClass}>
            free listing form
          </Link>
          . You provide your community name, platform, topic categories, member
          count, and a short description. Listings are reviewed for relevance
          and typically appear on up to two matching{" "}
          <Link to="/topics/" className={linkClass}>
            topic pages
          </Link>{" "}
          within a few days.
        </>
      ),
    },
    {
      question: "Is listing my community on Hive Index free?",
      answerPlain:
        "Yes. A basic listing on Hive Index is free forever. Free listings appear on up to two relevant topic pages and include your community name, platform, description, member count, and feature tags. Paid Featured and Gold options add more visibility and topic coverage.",
      answer:
        "Yes. A basic listing on Hive Index is free forever. Free listings appear on up to two relevant topic pages and include your community name, platform, description, member count, and feature tags. Paid Featured ($99/month) and Gold ($390/year) options add more visibility and topic coverage.",
    },
    {
      question: "What are the requirements to get listed on Hive Index?",
      answerPlain:
        "Your community needs at least 10 active members, must not promote NSFW or violent content, should have basic community features like chat or a forum, and must relate to a topic category already covered on Hive Index. Communities that do not meet these guidelines may be declined or removed.",
      answer: (
        <>
          Your community needs at least 10 active members, must not promote NSFW
          or violent content, should have basic{" "}
          <Link to="/features/" className={linkClass}>
            community features
          </Link>{" "}
          like chat or a forum, and must relate to a{" "}
          <Link to="/topics/" className={linkClass}>
            topic category
          </Link>{" "}
          already covered on Hive Index. Communities that do not meet these
          guidelines may be declined or removed.
        </>
      ),
    },
    {
      question:
        "What is the difference between free, Featured, and Gold listings?",
      answerPlain:
        "Free listings appear on up to two topic pages with standard placement. Featured ($99 one-time) puts your community at the top of one topic page for 30 days, on the homepage, and in similar-community recommendations, with a $1 CPC guarantee (99+ clicks). Gold Membership ($390/year) lists you on up to six topic pages with roughly 10x the traffic of free listings, social proof display, and more prominent placement across the site.",
      answer:
        "Free listings appear on up to two topic pages with standard placement. Featured ($99 one-time) puts your community at the top of one topic page for 30 days, on the homepage, and in similar-community recommendations, with a $1 CPC guarantee (99+ clicks). Gold Membership ($390/year) lists you on up to six topic pages with roughly 10x the traffic of free listings, social proof display, and more prominent placement across the site.",
    },
    {
      question: "How much traffic can I expect from a Hive Index listing?",
      answerPlain:
        "Traffic depends on listing type and topic. Free listings typically receive a handful of clicks per month. Featured listings average around 99+ referral clicks per month with the CPC guarantee. Gold listings receive roughly 10x the traffic of free listings. See the performance table on this page or visit the traffic stats page for current averages.",
      answer: (
        <>
          Traffic depends on listing type and topic. Free listings typically
          receive a handful of clicks per month. Featured listings average
          around 99+ referral clicks per month with the CPC guarantee. Gold
          listings receive roughly 10x the traffic of free listings. See the
          performance table on this page or visit the{" "}
          <Link to="/partners/traffic/" className={linkClass}>
            traffic stats page
          </Link>{" "}
          for current averages.
        </>
      ),
    },
    {
      question: "Can I promote my community on multiple topic pages?",
      answerPlain:
        "Yes. You can buy Featured promotions on multiple topic pages by increasing the quantity at checkout. Gold Membership covers up to six topic pages in a single subscription. You can also combine Featured promotions with Gold Membership for maximum visibility.",
      answer: (
        <>
          Yes. You can buy Featured promotions on multiple topic pages by
          increasing the quantity at checkout on the{" "}
          <Link to="/partners/community/" className={linkClass}>
            promotion page
          </Link>
          . Gold Membership covers up to six topic pages in a single
          subscription. You can also combine Featured promotions with Gold
          Membership for maximum visibility.
        </>
      ),
    },
    {
      question:
        "My community has not launched yet. Can I still submit it to Hive Index?",
      answerPlain:
        "Please wait until your community has at least 10 founding members before submitting. Hive Index lists active communities that prospective members can join today, not pre-launch waitlists.",
      answer:
        "Please wait until your community has at least 10 founding members before submitting. Hive Index lists active communities that prospective members can join today, not pre-launch waitlists.",
    },
    {
      question: "Can my community platform or tool sponsor Hive Index?",
      answerPlain:
        "Yes. Community platforms, tools, and agencies can sponsor Hive Index sitewide for $1,000/month. Sponsorship includes a banner on every page, homepage shoutout, and exit-screen placement. Visit the sponsorship page for details.",
      answer: (
        <>
          Yes. Community platforms, tools, and agencies can{" "}
          <Link to="/partners/sponsor/" className={linkClass}>
            sponsor Hive Index sitewide
          </Link>{" "}
          for $1,000/month. Sponsorship includes a banner on every page,
          homepage shoutout, and exit-screen placement.
        </>
      ),
    },
  ]
}

function communityPromotionPageFaq() {
  return [
    {
      question: "How much does it cost to promote my community on Hive Index?",
      answerPlain:
        "A Featured listing costs $99 for one month on a single topic page. Gold Membership costs $390 per year and covers up to six topic pages with ongoing elevated placement. Both options can be purchased through the payment links on this page.",
      answer:
        "A Featured listing costs $99 for one month on a single topic page. Gold Membership costs $390 per year and covers up to six topic pages with ongoing elevated placement. Both options can be purchased through the payment links on this page.",
    },
    {
      question: "What is a Featured listing on Hive Index?",
      answerPlain:
        "A Featured listing places your community at the top of one topic page for 30 days. It also appears on the Hive Index homepage, in similar-community recommendations, and comes with a $1 CPC guarantee: if you receive fewer than 99 unique referral clicks, the promotion keeps running until you reach 99.",
      answer:
        "A Featured listing places your community at the top of one topic page for 30 days. It also appears on the Hive Index homepage, in similar-community recommendations, and comes with a $1 CPC guarantee: if you receive fewer than 99 unique referral clicks, the promotion keeps running until you reach 99.",
    },
    {
      question: "What is Gold Membership on Hive Index?",
      answerPlain:
        "Gold Membership is a yearly subscription ($390/year) that lists your community on up to six relevant topic pages instead of two. Gold listings receive roughly 10x the organic traffic of free listings, show social proof (review scores and top reviews), and get more prominent placement in similar-community sections across the site.",
      answer:
        "Gold Membership is a yearly subscription ($390/year) that lists your community on up to six relevant topic pages instead of two. Gold listings receive roughly 10x the organic traffic of free listings, show social proof (review scores and top reviews), and get more prominent placement in similar-community sections across the site.",
    },
    {
      question: "How much traffic do Featured and Gold listings get?",
      answerPlain:
        "Featured listings average around 99+ referral clicks per month with the CPC guarantee, though results vary by topic. Gold listings receive roughly 10x the traffic of free listings on average. See the performance table on this page for current monthly click averages, cost per click, and traffic patterns by listing type.",
      answer:
        "Featured listings average around 99+ referral clicks per month with the CPC guarantee, though results vary by topic. Gold listings receive roughly 10x the traffic of free listings on average. See the performance table on this page for current monthly click averages, cost per click, and traffic patterns by listing type.",
    },
    {
      question: "Can I promote my community on more than one topic page?",
      answerPlain:
        "Yes. When purchasing a Featured listing, increase the quantity at checkout to cover multiple topic pages. Each quantity unit covers one topic page for one month. Gold Membership automatically covers up to six topic pages in a single subscription.",
      answer: (
        <>
          Yes. When purchasing a Featured listing, increase the quantity at
          checkout to cover multiple topic pages. Each quantity unit covers one
          topic page for one month. Gold Membership automatically covers up to
          six topic pages in a single subscription.
        </>
      ),
    },
    {
      question: "Can I combine a Featured promotion with Gold Membership?",
      answerPlain:
        "Yes. Combining Featured and Gold is the highest-traffic package available on Hive Index. Gold gives you broader topic coverage and elevated organic placement, while Featured adds a top-of-page boost on a specific topic for 30 days.",
      answer:
        "Yes. Combining Featured and Gold is the highest-traffic package available on Hive Index. Gold gives you broader topic coverage and elevated organic placement, while Featured adds a top-of-page boost on a specific topic for 30 days.",
    },
    {
      question: "Is there a discount for buying multiple months of promotion?",
      answerPlain:
        "Yes. Buy four months of Featured promotion (set quantity to 4 at checkout) and get one month free. Use the Featured promotion payment link and adjust the quantity before completing purchase.",
      answer: (
        <>
          Yes. Buy four months of Featured promotion (set quantity to 4 at{" "}
          <a href={communityPromotionLink} className={linkClass}>
            checkout
          </a>
          ) and get one month free.
        </>
      ),
    },
    {
      question: "Can I subscribe to ongoing Featured promotion?",
      answerPlain:
        "Yes. A monthly Featured subscription is available so you do not have to remember to renew each month. Use the promotion subscription payment link to set up recurring billing.",
      answer: (
        <>
          Yes. A monthly Featured subscription is available so you do not have
          to remember to renew each month. Use the{" "}
          <a href={communityPromotionSubscriptionLink} className={linkClass}>
            promotion subscription payment link
          </a>{" "}
          to set up recurring billing.
        </>
      ),
    },
    {
      question: "Can I edit my listing after purchasing a promotion?",
      answerPlain:
        "Yes. Email contact@thehiveindex.com to update your listing details, change topic assignments, or adjust promotion settings. Verified community owners can also claim their listing to manage updates directly.",
      answer: (
        <>
          Yes.{" "}
          <a href="mailto:contact@thehiveindex.com" className={linkClass}>
            Email us
          </a>{" "}
          to update your listing details, change topic assignments, or adjust
          promotion settings. Verified community owners can also claim their
          listing to manage updates directly.
        </>
      ),
    },
  ]
}

function sponsorPageFaq() {
  return [
    {
      question: "What is Hive Index site sponsorship?",
      answerPlain:
        "Hive Index site sponsorship is a one-month, sitewide brand placement that puts your company in front of 25,000+ monthly visitors who browse, compare, and join online communities. Your ad appears as a text-based banner at the top of every page, on the homepage, in the site footer, and on the community exit screen.",
      answer:
        "Hive Index site sponsorship is a one-month, sitewide brand placement that puts your company in front of 25,000+ monthly visitors who browse, compare, and join online communities. Your ad appears as a text-based banner at the top of every page, on the homepage, in the site footer, and on the community exit screen.",
    },
    {
      question: "Who should sponsor Hive Index?",
      answerPlain:
        "Site sponsorship works best for community platforms (Discord alternatives, Circle, Slack apps), community tools and analytics products, newsletter or creator tools, agencies serving community-led businesses, and brands targeting founders, marketers, creators, and niche community operators.",
      answer:
        "Site sponsorship works best for community platforms (Discord alternatives, Circle, Slack apps), community tools and analytics products, newsletter or creator tools, agencies serving community-led businesses, and brands targeting founders, marketers, creators, and niche community operators.",
    },
    {
      question: "What does a Hive Index sponsorship include?",
      answerPlain:
        "Each one-month sponsorship includes a 'Brought to you by' link in the banner on every page (100,000+ monthly pageviews), a shoutout on the homepage and site footer, and your ad on the community exit screen shown when visitors click through to join a community.",
      answer:
        "Each one-month sponsorship includes a 'Brought to you by' link in the banner on every page (100,000+ monthly pageviews), a shoutout on the homepage and site footer, and your ad on the community exit screen shown when visitors click through to join a community.",
    },
    {
      question: "How much does it cost to sponsor Hive Index?",
      answerPlain:
        "Site sponsorship costs $1,000 for one month. There is one exclusive sponsor slot per month. Purchase through the sponsorship payment link on this page.",
      answer: (
        <>
          Site sponsorship costs $1,000 for one month. There is one exclusive
          sponsor slot per month. Purchase through the{" "}
          <a href={sponsorPromotionLink} className={linkClass}>
            sponsorship payment link
          </a>{" "}
          on this page.
        </>
      ),
    },
    {
      question: "What can I promote with a Hive Index sponsorship?",
      answerPlain:
        "You can promote any business that aligns with the Hive Index audience. Best results come from community platforms, community management tools, creator or newsletter products, and services for community builders. Off-topic or unrelated products perform poorly with this audience.",
      answer:
        "You can promote any business that aligns with the Hive Index audience. Best results come from community platforms, community management tools, creator or newsletter products, and services for community builders. Off-topic or unrelated products perform poorly with this audience.",
    },
    {
      question: "How many people will see my Hive Index sponsorship?",
      answerPlain:
        "Hive Index receives roughly 25,000 unique visitors and 100,000+ pageviews per month. Your sponsorship banner appears at the top of every page, so impressions scale with total site traffic during your sponsorship month.",
      answer:
        "Hive Index receives roughly 25,000 unique visitors and 100,000+ pageviews per month. Your sponsorship banner appears at the top of every page, so impressions scale with total site traffic during your sponsorship month.",
    },
    {
      question: "Have questions before sponsoring Hive Index?",
      answerPlain:
        "Email contact@thehiveindex.com with your product, target audience, and goal. We will follow up to confirm fit and scheduling for the exclusive monthly sponsor slot.",
      answer: (
        <>
          Email{" "}
          <a
            href="mailto:contact@thehiveindex.com?subject=Sponsorship"
            className={linkClass}
          >
            contact@thehiveindex.com
          </a>{" "}
          with your product, target audience, and goal. We will follow up to
          confirm fit and scheduling for the exclusive monthly sponsor slot.
        </>
      ),
    },
  ]
}

function paidAdPageFaq() {
  return [
    {
      question: "What is a Hive Index topic page ad?",
      answerPlain:
        "A topic page ad is a lightweight text or display ad placed on one Hive Index topic page for one month. Topic pages are where people browse communities in a specific niche, like entrepreneurship, design, fitness, or gaming. Ads reach visitors with clear intent to explore or join communities in that category.",
      answer: (
        <>
          A topic page ad is a lightweight text or display ad placed on one{" "}
          <Link to="/topics/" className={linkClass}>
            Hive Index topic page
          </Link>{" "}
          for one month. Topic pages are where people browse communities in a
          specific niche, like entrepreneurship, design, fitness, or gaming. Ads
          reach visitors with clear intent to explore or join communities in
          that category.
        </>
      ),
    },
    {
      question: "Where does my ad appear on Hive Index?",
      answerPlain:
        "Your ad appears in the right sidebar of the topic page you select. The same placement also shows on individual community profile pages that belong to that topic, giving your offer repeated exposure as visitors browse listings.",
      answer:
        "Your ad appears in the right sidebar of the topic page you select. The same placement also shows on individual community profile pages that belong to that topic, giving your offer repeated exposure as visitors browse listings.",
    },
    {
      question: "How is topic page ad pricing calculated?",
      answerPlain:
        "Pricing starts at $50 per month and increases in $50 increments for higher-traffic topic pages. The price shown when you select a topic reflects that page's recent viewership. You can compare topic traffic before choosing.",
      answer:
        "Pricing starts at $50 per month and increases in $50 increments for higher-traffic topic pages. The price shown when you select a topic reflects that page's recent viewership. You can compare topic traffic before choosing.",
    },
    {
      question: "What can I advertise on a Hive Index topic page?",
      answerPlain:
        "Topic page ads work for communities, tools, newsletters, events, job boards, courses, lead magnets, and other offers relevant to the topic audience. The ad links to your landing page and uses short text copy you provide after checkout.",
      answer:
        "Topic page ads work for communities, tools, newsletters, events, job boards, courses, lead magnets, and other offers relevant to the topic audience. The ad links to your landing page and uses short text copy you provide after checkout.",
    },
    {
      question: "Can I change topics or pause my topic page ad?",
      answerPlain:
        "Topic page ads are month-to-month. You can buy one month at a time or subscribe for ongoing placement. Email contact@thehiveindex.com to switch topics, update ad copy, or pause between billing cycles.",
      answer: (
        <>
          Topic page ads are month-to-month. You can buy one month at a time or
          subscribe for ongoing placement. Email{" "}
          <a
            href="mailto:contact@thehiveindex.com?subject=Paid Ad Question"
            className={linkClass}
          >
            contact@thehiveindex.com
          </a>{" "}
          to switch topics, update ad copy, or pause between billing cycles.
        </>
      ),
    },
    {
      question: "How do I get started with a Hive Index topic page ad?",
      answerPlain:
        "Pick a topic page from the dropdown, choose one month or a monthly subscription, and complete checkout. After payment, Hive Index follows up by email for your link and ad copy. Your ad goes live once copy is approved, typically within a few business days.",
      answer:
        "Pick a topic page from the dropdown on this page, choose one month or a monthly subscription, and complete checkout. After payment, Hive Index follows up by email for your link and ad copy. Your ad goes live once copy is approved, typically within a few business days.",
    },
    {
      question:
        "How is a topic page ad different from a Featured community listing?",
      answerPlain:
        "A Featured listing promotes your own community at the top of a topic page. A topic page ad promotes any offer (tool, newsletter, event, etc.) in the sidebar. Featured listings are for community builders growing membership. Topic page ads are for advertisers reaching an existing niche audience.",
      answer: (
        <>
          A{" "}
          <Link to="/partners/community/" className={linkClass}>
            Featured listing
          </Link>{" "}
          promotes your own community at the top of a topic page. A topic page
          ad promotes any offer (tool, newsletter, event, etc.) in the sidebar.
          Featured listings are for community builders growing membership. Topic
          page ads are for advertisers reaching an existing niche audience.
        </>
      ),
    },
  ]
}

function platformsIndexFaq({ platformCount, communityCount }) {
  return [
    {
      question: "What community platforms does Hive Index cover?",
      answerPlain: `Hive Index lists online communities across ${platformCount}+ platforms, including Discord, Slack, Reddit, Circle, Facebook Groups, LinkedIn Groups, Telegram, and independent forums. Each platform page shows every community in the directory hosted on that platform, organized by topic.`,
      answer: (
        <>
          Hive Index lists online communities across {platformCount}+ platforms,
          including Discord, Slack, Reddit, Circle, Facebook Groups, LinkedIn
          Groups, Telegram, and independent forums. Each platform page shows
          every community in the directory hosted on that platform, organized by
          topic.
        </>
      ),
    },
    {
      question: "How do I find communities on a specific platform?",
      answerPlain:
        "Browse the platform list on this page and click any platform to see all communities hosted there. From a platform page you can filter by topic, member size, and features, then click through to join a community directly.",
      answer: (
        <>
          Browse the platform list on this page and click any platform to see
          all communities hosted there. From a platform page you can filter by{" "}
          <Link to="/topics/" className={linkClass}>
            topic
          </Link>
          , member size, and{" "}
          <Link to="/features/" className={linkClass}>
            features
          </Link>
          , then click through to join a community directly.
        </>
      ),
    },
    {
      question:
        "What is the difference between community platforms on Hive Index?",
      answerPlain:
        "Each platform shapes how members interact. Discord and Slack emphasize real-time chat. Reddit uses threaded public discussions. Circle and Mighty Networks offer paid membership and course features. Independent platforms vary widely. Hive Index tags each listing with platform, features, and topic so you can compare before joining.",
      answer:
        "Each platform shapes how members interact. Discord and Slack emphasize real-time chat. Reddit uses threaded public discussions. Circle and Mighty Networks offer paid membership and course features. Independent platforms vary widely. Hive Index tags each listing with platform, features, and topic so you can compare before joining.",
    },
    {
      question: "How many communities are listed on each platform?",
      answerPlain: `Community counts vary by platform and are shown on each platform card on this page. Hive Index currently tracks ${communityCount.toLocaleString()}+ communities total across all platforms, with counts updated as new communities are submitted and reviewed.`,
      answer: (
        <>
          Community counts vary by platform and are shown on each platform card
          on this page. Hive Index currently tracks{" "}
          {communityCount.toLocaleString()}+ communities total across all
          platforms, with counts updated as new communities are submitted and
          reviewed.
        </>
      ),
    },
    {
      question: "Can I suggest a platform that is missing from Hive Index?",
      answerPlain:
        "Yes. Use the Suggest page to recommend a platform or community that is not yet in the directory. Hive Index adds new platforms as communities on them are discovered and submitted.",
      answer: (
        <>
          Yes. Use the{" "}
          <Link to="/suggest/" className={linkClass}>
            Suggest page
          </Link>{" "}
          to recommend a platform or community that is not yet in the directory.
          Hive Index adds new platforms as communities on them are discovered
          and submitted.
        </>
      ),
    },
    {
      question: "How do I list my community on Hive Index?",
      answerPlain:
        "Submit your community for free through the Hive Index listing form. Free listings appear on up to two relevant topic pages. Paid Featured and Gold options add more visibility across topic and platform pages.",
      answer: (
        <>
          Submit your community for free through the{" "}
          <Link to="/submit/" className={linkClass}>
            Hive Index listing form
          </Link>
          . Free listings appear on up to two relevant topic pages. Paid
          Featured and Gold options add more visibility across topic and
          platform pages.
        </>
      ),
    },
  ]
}

function featuresIndexFaq({ featureCount, communityCount }) {
  return [
    {
      question: "What are community feature tags on Hive Index?",
      answerPlain: `Feature tags describe how a community works, not just what it is about. Hive Index tags each of ${communityCount.toLocaleString()}+ listings with features like forums, live chat, events, job boards, courses, paid membership, member pairing, and newsletters. There are ${featureCount} feature categories you can browse or filter by.`,
      answer: (
        <>
          Feature tags describe how a community works, not just what it is
          about. Hive Index tags each of {communityCount.toLocaleString()}+
          listings with features like forums, live chat, events, job boards,
          courses, paid membership, member pairing, and newsletters. There are{" "}
          {featureCount} feature categories you can browse or filter by.
        </>
      ),
    },
    {
      question: "How do I filter communities by features on Hive Index?",
      answerPlain:
        "Start on any topic page and use the feature icon filters above the community list. You can also browse a feature directly from this page to see all communities tagged with that feature, then narrow by topic or platform.",
      answer: (
        <>
          Start on any{" "}
          <Link to="/topics/" className={linkClass}>
            topic page
          </Link>{" "}
          and use the feature icon filters above the community list. You can
          also browse a feature directly from this page to see all communities
          tagged with that feature, then narrow by topic or platform.
        </>
      ),
    },
    {
      question: "What features can I search for on Hive Index?",
      answerPlain:
        "Available features include Forum (threaded discussions), Chat (real-time messaging), Events, Jobs, Courses, Newsletter, Paid membership, Member pairing, Application-only access, Perks, Tools, and Token or web3 access. Each feature page lists every community in the directory with that tag.",
      answer:
        "Available features include Forum (threaded discussions), Chat (real-time messaging), Events, Jobs, Courses, Newsletter, Paid membership, Member pairing, Application-only access, Perks, Tools, and Token or web3 access. Each feature page lists every community in the directory with that tag.",
    },
    {
      question: "How do feature tags relate to topic pages on Hive Index?",
      answerPlain:
        "Topic pages organize communities by subject (e.g. Entrepreneurship, Design). Feature tags describe the experience within those communities. Hive Index also has feature-plus-topic pages (e.g. Entrepreneurship communities with a job board) for more specific discovery.",
      answer: (
        <>
          <Link to="/topics/" className={linkClass}>
            Topic pages
          </Link>{" "}
          organize communities by subject (e.g. Entrepreneurship, Design).
          Feature tags describe the experience within those communities. Hive
          Index also has feature-plus-topic pages (e.g. Entrepreneurship
          communities with a job board) for more specific discovery.
        </>
      ),
    },
    {
      question: "Can I suggest a new feature tag for Hive Index?",
      answerPlain:
        "Yes. If your community has a feature not yet represented in the directory, use the Suggest page to recommend it. Feature tags are added when enough communities share the same capability.",
      answer: (
        <>
          Yes. If your community has a feature not yet represented in the
          directory, use the{" "}
          <Link to="/suggest/" className={linkClass}>
            Suggest page
          </Link>{" "}
          to recommend it. Feature tags are added when enough communities share
          the same capability.
        </>
      ),
    },
    {
      question:
        "Do communities need certain features to be listed on Hive Index?",
      answerPlain:
        "Communities should have at least one core community feature, such as a forum or live chat. Listings without basic interaction features (e.g. a static website with no member space) may not be accepted.",
      answer: (
        <>
          Communities should have at least one core community feature, such as a
          forum or live chat. Listings without basic interaction features may
          not be accepted. See{" "}
          <Link to="/submit/" className={linkClass}>
            listing requirements
          </Link>{" "}
          for details.
        </>
      ),
    },
  ]
}

/** Map FAQ entries to FaqSection `{ q, a }` props. */
function faqToSectionProps(faq) {
  return faq.map(item => ({
    q: item.question,
    a: item.answer ?? item.answerPlain,
  }))
}

export {
  submitPageFaq,
  communityPromotionPageFaq,
  sponsorPageFaq,
  paidAdPageFaq,
  platformsIndexFaq,
  featuresIndexFaq,
  faqToSectionProps,
}
